import { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { type Request, RequestPayloadCreateUser, RequestPayloadUpdateRole, RequestPayloadCreateProject } from '@/services/requestService'
import { RequestPayloadUpdateProjectRole, RequestPayloadAddProjectMember, RequestPayloadRemoveProjectMember } from '@/services/projectRequestService'
import { userService, type User } from '@/services/userService'
import PlatformUserDetailsModal, { type PlatformUserDetailsModalRef } from './PlatformUserDetailsModal'

interface RequestDetailsModalProps {}

export interface RequestDetailsModalRef {
  open: (request: Request) => void
  close: () => void
}

const RequestDetailsModal = forwardRef<RequestDetailsModalRef, RequestDetailsModalProps>((props, ref) => {
  const { t } = useTranslation('requests')
  const [isOpen, setIsOpen] = useState(false)
  const [request, setRequest] = useState<Request | null>(null)
  const [projectOwner, setProjectOwner] = useState<User | null>(null)
  const [targetUser, setTargetUser] = useState<User | null>(null)
  const [loadingOwner, setLoadingOwner] = useState(false)
  const [loadingTargetUser, setLoadingTargetUser] = useState(false)
  const userDetailsModalRef = useRef<PlatformUserDetailsModalRef>(null)

  useImperativeHandle(ref, () => ({
    open: (requestData: Request) => {
      setRequest(requestData)
      setIsOpen(true)
    },
    close: () => {
      setIsOpen(false)
      setRequest(null)
      setProjectOwner(null)
      setTargetUser(null)
    },
  }))

  // Fetch project owner when request is create_project type
  useEffect(() => {
    const fetchProjectOwner = async () => {
      if (!request || request.type !== 'create_project') {
        setProjectOwner(null)
        return
      }

      try {
        const payload = JSON.parse(request.payload) as RequestPayloadCreateProject
        if (!payload.owner_id) return

        setLoadingOwner(true)
        const owner = await userService.getUserById(payload.owner_id)
        setProjectOwner(owner)
      } catch (err) {
        console.error('Failed to fetch project owner:', err)
        setProjectOwner(null)
      } finally {
        setLoadingOwner(false)
      }
    }

    fetchProjectOwner()
  }, [request])

  // Fetch target user when request contains user_id
  useEffect(() => {
    const fetchTargetUser = async () => {
      if (!request) {
        setTargetUser(null)
        return
      }

      // Check if request type contains user_id
      const typesWithUserId = ['update_platform_role', 'update_project_role', 'add_project_member', 'remove_project_member']
      if (!typesWithUserId.includes(request.type)) {
        setTargetUser(null)
        return
      }

      try {
        const payload = JSON.parse(request.payload)
        if (!payload.user_id) return

        setLoadingTargetUser(true)
        const user = await userService.getUserById(payload.user_id)
        setTargetUser(user)
      } catch (err) {
        console.error('Failed to fetch target user:', err)
        setTargetUser(null)
      } finally {
        setLoadingTargetUser(false)
      }
    }

    fetchTargetUser()
  }, [request])

  const handleClose = () => {
    setIsOpen(false)
    setRequest(null)
    setProjectOwner(null)
    setTargetUser(null)
  }

  if (!isOpen || !request) return null

  // Component to render formatted JSON
  const JsonDisplay = ({ data }: { data: any }) => {
    return (
      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
        <pre className="text-xs text-gray-100 font-mono leading-relaxed whitespace-pre">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    )
  }

  const parsePayload = () => {
    try {
      const payload = JSON.parse(request.payload)

      // Handle resource-related requests with formatted JSON display
      const resourceTypes = [
        'create_resource',
        'update_resource',
        'delete_resource',
        'create_flow',
        'update_flow',
        'delete_flow',
        'restart_deployment'
      ]

      if (resourceTypes.some(type => request.type.includes(type)) ||
          (payload.manifest && typeof payload.manifest === 'object')) {
        // If payload contains a manifest, show it formatted
        return (
          <div className="space-y-3">
            {payload.manifest ? (
              <>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-700 mb-2">{t('detailsModal.manifest')}:</span>
                </div>
                <JsonDisplay data={payload.manifest} />
              </>
            ) : (
              <JsonDisplay data={payload} />
            )}
          </div>
        )
      }

      if (request.type === 'create_user') {
        const p = payload as RequestPayloadCreateUser
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.account')}:</span>
              <span className="text-sm text-gray-900">{p.account}</span>
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.name')}:</span>
              <span className="text-sm text-gray-900">{p.name}</span>
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.role')}:</span>
              <span className="text-sm text-gray-900">{t(`roles.${p.role}`)}</span>
            </div>
          </div>
        )
      } else if (request.type === 'update_platform_role') {
        const p = payload as RequestPayloadUpdateRole
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.userId')}:</span>
              {loadingTargetUser ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : targetUser ? (
                <button
                  onClick={() => userDetailsModalRef.current?.open(targetUser)}
                  className="text-sm text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
                >
                  <div className="font-medium">{targetUser.name}</div>
                  <div className="text-xs text-gray-500">@{targetUser.account}</div>
                </button>
              ) : (
                <span className="text-sm text-gray-900 break-all">{p.user_id}</span>
              )}
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.newRole')}:</span>
              <span className="text-sm text-gray-900">{t(`roles.${p.role}`)}</span>
            </div>
          </div>
        )
      } else if (request.type === 'create_project') {
        const p = payload as RequestPayloadCreateProject
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.projectName')}:</span>
              <span className="text-sm text-gray-900">{p.name}</span>
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.description')}:</span>
              <span className="text-sm text-gray-900">{p.description}</span>
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.owner')}:</span>
              {loadingOwner ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : projectOwner ? (
                <button
                  onClick={() => userDetailsModalRef.current?.open(projectOwner)}
                  className="text-sm text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
                >
                  <div className="font-medium">{projectOwner.name}</div>
                  <div className="text-xs text-gray-500">@{projectOwner.account}</div>
                </button>
              ) : (
                <span className="text-sm text-gray-900 break-all">{p.owner_id}</span>
              )}
            </div>
          </div>
        )
      } else if (request.type === 'update_project_role') {
        const p = payload as RequestPayloadUpdateProjectRole
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.userId')}:</span>
              {loadingTargetUser ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : targetUser ? (
                <button
                  onClick={() => userDetailsModalRef.current?.open(targetUser)}
                  className="text-sm text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
                >
                  <div className="font-medium">{targetUser.name}</div>
                  <div className="text-xs text-gray-500">@{targetUser.account}</div>
                </button>
              ) : (
                <span className="text-sm text-gray-900 break-all">{p.user_id}</span>
              )}
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.newRole')}:</span>
              <span className="text-sm text-gray-900">{t(`roles.${p.role}`)}</span>
            </div>
          </div>
        )
      } else if (request.type === 'add_project_member') {
        const p = payload as RequestPayloadAddProjectMember
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.userId')}:</span>
              {loadingTargetUser ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : targetUser ? (
                <button
                  onClick={() => userDetailsModalRef.current?.open(targetUser)}
                  className="text-sm text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
                >
                  <div className="font-medium">{targetUser.name}</div>
                  <div className="text-xs text-gray-500">@{targetUser.account}</div>
                </button>
              ) : (
                <span className="text-sm text-gray-900 break-all">{p.user_id}</span>
              )}
            </div>
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.role')}:</span>
              <span className="text-sm text-gray-900">{t(`roles.${p.role}`)}</span>
            </div>
          </div>
        )
      } else if (request.type === 'remove_project_member') {
        const p = payload as RequestPayloadRemoveProjectMember
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-32 text-sm font-medium text-gray-700">{t('detailsModal.userId')}:</span>
              {loadingTargetUser ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : targetUser ? (
                <button
                  onClick={() => userDetailsModalRef.current?.open(targetUser)}
                  className="text-sm text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
                >
                  <div className="font-medium">{targetUser.name}</div>
                  <div className="text-xs text-gray-500">@{targetUser.account}</div>
                </button>
              ) : (
                <span className="text-sm text-gray-900 break-all">{p.user_id}</span>
              )}
            </div>
          </div>
        )
      }

      // For any other unhandled types, try to display as formatted JSON
      return <JsonDisplay data={payload} />
    } catch {
      // If JSON parsing fails, show raw payload in formatted way
      return (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-100 font-mono leading-relaxed whitespace-pre">
            {request.payload}
          </pre>
        </div>
      )
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('detailsModal.title')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('detailsModal.subtitle')}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Request Type & Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">{t('detailsModal.requestType')}</p>
                <p className="text-sm font-semibold text-gray-900">{t(`requestTypes.${request.type}`)}</p>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                request.status === 'approved' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {t(`status.${request.status}`)}
              </span>
            </div>
          </div>

          {/* Requestor Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('detailsModal.requestorInfo')}</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('detailsModal.requestorName')}:</span>
                <span className="text-sm text-gray-900">{request.requestor.name || '-'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('detailsModal.requestorAccount')}:</span>
                <span className="text-sm text-gray-900">{request.requestor.account || '-'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('detailsModal.createdAt')}:</span>
                <span className="text-sm text-gray-900">{formatDate(request.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Approver Info (if approved/rejected) */}
          {(request.status === 'approved' || request.status === 'rejected') && request.approver && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('detailsModal.approverInfo')}</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{t('detailsModal.approverName')}:</span>
                  <span className="text-sm text-gray-900">{request.approver.name || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{t('detailsModal.approverAccount')}:</span>
                  <span className="text-sm text-gray-900">{request.approver.account || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{t('detailsModal.processedAt')}:</span>
                  <span className="text-sm text-gray-900">{request.processed_at ? formatDate(request.processed_at) : '-'}</span>
                </div>
                {request.reason && (
                  <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-700">{t('detailsModal.reason')}:</span>
                    <span className="text-sm text-gray-900 flex-1">{request.reason}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Request Details */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('detailsModal.requestDetails')}</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {parsePayload()}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('detailsModal.close')}
          </button>
        </div>
      </div>
      </div>

      {/* User Details Modal */}
      <PlatformUserDetailsModal ref={userDetailsModalRef} />
    </>
  )
})

RequestDetailsModal.displayName = 'RequestDetailsModal'

export default RequestDetailsModal
